import { useCallback, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  TRANSFERRED_EVENT_REGISTRATION_EMAIL_PREVIEW_QUERY,
  type EmailPreview,
  type TransferredEventRegistrationEmailPreviewInput,
  type TransferredEventRegistrationEmailPreviewQueryResponse,
  type TransferredEventRegistrationEmailPreviewQueryVariables
} from "../_queries/emailPreviewQueries";

export function useTransferredEventRegistrationEmailPreview() {
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  const [fetchPreview] = useLazyQuery<
    TransferredEventRegistrationEmailPreviewQueryResponse,
    TransferredEventRegistrationEmailPreviewQueryVariables
  >(TRANSFERRED_EVENT_REGISTRATION_EMAIL_PREVIEW_QUERY, { fetchPolicy: "network-only" });

  const resetPreview = useCallback(() => {
    activeRequestIdRef.current += 1;
    setPreview(null);
    setError(null);
    setLoading(false);
  }, []);

  const loadPreview = useCallback(
    async (input: TransferredEventRegistrationEmailPreviewInput) => {
      const requestId = activeRequestIdRef.current + 1;
      activeRequestIdRef.current = requestId;

      setLoading(true);
      setPreview(null);
      setError(null);

      const result = await fetchPreview({
        variables: { input }
      });

      if (requestId !== activeRequestIdRef.current) {
        return result;
      }

      if (result.error) {
        setError(result.error);
        setPreview(null);
      } else {
        setPreview(result.data?.transferredEventRegistrationEmailPreview ?? null);
        setError(null);
      }

      setLoading(false);
      return result;
    },
    [fetchPreview]
  );

  return {
    loadPreview,
    resetPreview,
    preview,
    loading,
    error
  };
}
